/*
 * Słodki Bufet sets by count (DECISIONS 2 Nov 2026, his rules): every tap puts the jar in the cart and the number of jars of
 * a kind alone decides the price. Under the first step (6 mixes) each jar is at its own price; from the first step every jar
 * is at the first step's price; from the last step (8) every jar, however many, is at the last step's price. Nothing is
 * chosen and a full set never blocks adding more.
 *
 * A set is one line of the hidden set product per price class (variant SKU „…-SIZE-CLASS”, price = one jar of that class
 * at that step), quantity = its jars, with „Skład”, `_zestaw`, `_sklad` („handle:variant” per jar), `_nr` (one per set,
 * shared by its class lines) and `_rozmiar` (the step). The hub splits such a line into its jars in BaseLinker.
 *
 * Between two steps every jar is at the lower step's price, never a mix of set and single prices (his rule, 2 Nov: „jak masz
 * 7 mixów to 7 powinien być też w cenie tych sześciu … przy gliterach ta sama zasada”): 7 mixes = 7 × the 6 price, 4 glitters
 * = 4 × the 3 price.
 *
 * Christmas jars never count (his rule 2 Nov: „świąteczne produkty nie wchodzą w zestawy”; they get ready-made sets of their
 * own later, as another `def`): the theme marks them `out` and leaves `elig` empty, and `plan` skips a line marked `out` even
 * if `elig` says otherwise.
 *
 * `plan` is pure: it reads the cart data sb-cart.js already has and returns what to add and what to update, or null when
 * the cart already holds the right lines. `step` writes a plan: add first, remove second, and take the added lines out
 * again when the second write fails, so a jar is never charged twice.
 *
 * Another page of the shop may be pricing the same cart (2 Nov, real cart: a second tab or a prerendered page planned the
 * same swap from its own old copy, and 6 mixes came back as 12 at the 8 price). So `step` runs under a lock shared by every
 * page, reads the cart itself first, and writes nothing when the copy it would plan from is not the cart any more.
 *
 * A page can also go away between the two writes (3 Nov, real cart: the page reloaded after the add landed and before the
 * old lines were taken out, and the next page priced 6 + 2 + 8 jars as a set of 16). So before the add `step` writes down
 * what it is doing (`io.journal`, shared by every page), and the next pass on any page first finishes a swap whose add
 * landed: each line it was taking out still at its old quantity goes to the planned one.
 */
(() => {
  const tierOf = (n, sizes) => sizes.filter((s) => s <= n).sort((a, b) => b - a)[0] || 0;
  const topOf = (sizes) => Math.max(0, ...sizes);

  /** How many of `n` jars are in the set and at which step: from the first step all of them. */
  function shape(n, def) {
    const tier = tierOf(n, def.sizes);
    return { tier, inSet: tier ? n : 0 };
  }

  /** The set's price per jar of a class at the step that `n` jars reach (0 below the first step). */
  function unitFor(n, def, cls) {
    const { tier } = shape(n, def);
    if (!tier) return 0;
    const v = def.variants.find((x) => x.size === tier && x.cls === cls);
    return v ? v.u : 0;
  }

  // Liquid hands line item properties over HTML-escaped (BLACK&#39;N&#39;GOLD); written back as they are, Shopify escapes them
  // again and the checkout shows „BLACK&amp;#39;N…” (real cart, 2 Nov)
  const ENTITIES = { '#39': "'", '#x27': "'", quot: '"', lt: '<', gt: '>', amp: '&' };
  function unescapeHtml(text) {
    let out = String(text || '');
    for (let i = 0; i < 3 && /&(#39|#x27|quot|lt|gt|amp);/.test(out); i += 1) out = out.replace(/&(#39|#x27|quot|lt|gt|amp);/g, (m, e) => ENTITIES[e]);
    return out;
  }

  /** Titles of a set line's jars from its „Skład” („A ×2, B”), matched to the handles in `_sklad` by first appearance. */
  function shownTitles(line) {
    const out = new Map();
    const handles = [];
    (line.comps || []).forEach((c) => {
      if (!handles.includes(c.h)) handles.push(c.h);
    });
    const parts = unescapeHtml(line.shown)
      .split(', ')
      .map((s) => s.replace(/ ×\d+$/, '').trim())
      .filter(Boolean);
    if (parts.length === handles.length) handles.forEach((h, i) => out.set(h, parts[i]));
    return out;
  }

  const nameOf = (h) => h.replace(/-/g, ' ').toUpperCase();
  const bag = (jars) => jars.map((j) => `${j.h}:${j.v}`).sort().join(',');

  /**
   * @param {object} d - the cart data (`lines`: key, variant, qty, set, elig, out, cls, nr, size, comps, shown, handle, title, regular,
   *   stock).
   * @param {object} def - the set: id, sizes, variants [{size, cls, v, u}].
   * @param {object} [opts] - `drop`: {key, v}, one jar taken out of a set line (the cart's „Usuń” next to it).
   * @returns {null|{items: object[], updates: object, n: number, tier: number, total: number, over: object[]}} `over`: singles cut to
   *   their jar's stock ({v, title, cut, stock}).
   */
  function plan(d, def, opts = {}) {
    const lines = d.lines || [];
    const setLines = lines.filter((l) => l.set === def.id).reverse(); // oldest first: Shopify lists the newest first
    const singles = lines.filter((l) => l.elig === def.id && !l.out).reverse();
    const titles = new Map();
    singles.forEach((l) => titles.set(l.handle, l.title));
    setLines.forEach((l) => shownTitles(l).forEach((t, h) => titles.has(h) || titles.set(h, t)));

    const jars = [];
    let dropped = false;
    setLines.forEach((l) => {
      const comps = [...(l.comps || [])];
      if (opts.drop && !dropped && String(opts.drop.key) === String(l.key)) {
        const i = comps.findIndex((c) => String(c.v) === String(opts.drop.v));
        if (i >= 0) {
          comps.splice(i, 1);
          dropped = true;
        }
      }
      comps.forEach((c) => jars.push({ h: c.h, v: c.v, cls: l.cls }));
    });
    // Stock (3 Nov, real cart): a set line is the set product, which keeps no stock, so a jar inside it is never checked against
    // its own stock, and a second jar of the last one in stock got into the set once the first one was in it. A single of a jar
    // goes in only as far as the jar's stock (`stock`, from the cart data; none = not counted) allows; the rest leaves the cart.
    const held = new Map();
    jars.forEach((j) => held.set(String(j.v), (held.get(String(j.v)) || 0) + 1));
    const over = [];
    singles.forEach((l) => {
      let cut = 0;
      for (let i = 0; i < l.qty; i += 1) {
        const k = String(l.variant);
        if (typeof l.stock === 'number' && (held.get(k) || 0) >= l.stock) {
          cut += 1;
          continue;
        }
        held.set(k, (held.get(k) || 0) + 1);
        jars.push({ h: l.handle, v: l.variant, cls: l.cls });
      }
      if (cut) over.push({ v: l.variant, title: l.title, cut, stock: l.stock });
    });
    if (opts.drop && !dropped) return null;

    const n = jars.length;
    const { tier, inSet } = shape(n, def);
    // the dearer jars go into the set first (it saves the customer most); otherwise the order in the cart is kept
    const order = jars.map((j, i) => ({ j, i })).sort((a, b) => b.j.cls - a.j.cls || a.i - b.i);
    const inJars = order.slice(0, inSet).map((x) => x.j);
    const outJars = order.slice(inSet).map((x) => x.j);

    const byClass = new Map();
    inJars.forEach((j) => {
      if (!byClass.has(j.cls)) byClass.set(j.cls, []);
      byClass.get(j.cls).push(j);
    });
    const target = [];
    for (const [cls, js] of byClass) {
      const v = def.variants.find((x) => x.size === tier && x.cls === cls);
      if (!v) return null; // a price class without a set variant: leave the cart as it is
      target.push({ cls, v, jars: js });
    }

    // nothing to do when every class has one set line at the right variant with the same jars, one set number, and the
    // single lines hold exactly the jars outside the set
    const want = new Map();
    outJars.forEach((j) => want.set(j.v, (want.get(j.v) || 0) + 1));
    const have = new Map();
    singles.forEach((l) => have.set(l.variant, (have.get(l.variant) || 0) + l.qty));
    const sameSingles = want.size === have.size && [...want].every(([v, q]) => have.get(v) === q);
    const sameSets =
      setLines.length === target.length &&
      new Set(setLines.map((l) => l.nr || l.key)).size <= 1 &&
      target.every((t) => {
        const l = setLines.find((x) => x.cls === t.cls);
        return (
          l &&
          l.variant === t.v.v &&
          l.qty === t.jars.length &&
          (l.comps || []).length === l.qty &&
          Number(l.size) === tier &&
          bag((l.comps || []).map((c) => ({ h: c.h, v: c.v }))) === bag(t.jars)
        );
      });
    if (!dropped && !over.length && sameSingles && sameSets) return null;

    // a new set number, never one already in the cart: same properties would merge the new line into the old one
    const taken = new Set(setLines.map((l) => l.nr));
    let nr = Date.now().toString(36);
    for (let i = 1; taken.has(nr); i += 1) nr = `${Date.now().toString(36)}${i}`;
    const osobno = inJars.reduce((sum, j) => sum + j.cls, 0);
    const items = target.map((t) => {
      const counts = new Map();
      t.jars.forEach((j) => {
        const name = titles.get(j.h) || nameOf(j.h);
        counts.set(name, (counts.get(name) || 0) + 1);
      });
      return {
        id: t.v.v,
        quantity: t.jars.length,
        properties: {
          Skład: [...counts].map(([name, c]) => (c > 1 ? `${name} ×${c}` : name)).join(', '),
          _zestaw: def.id,
          _sklad: t.jars.map((j) => `${j.h}:${j.v}`).join(','),
          _osobno: String(osobno),
          _nr: nr,
          _rozmiar: String(tier),
        },
      };
    });
    const updates = {};
    setLines.forEach((l) => (updates[l.key] = 0));
    // the jars outside the set: the first single line of a variant takes them all, so nothing added below merges into a
    // line that is also being updated
    const need = new Map(want);
    singles.forEach((l) => {
      const q = need.get(l.variant) || 0;
      need.set(l.variant, 0);
      if (q !== l.qty) updates[l.key] = q;
    });
    need.forEach((q, v) => {
      if (q > 0) items.push({ id: v, quantity: q, properties: { _sb: 'zestaw' } });
    });
    const total = target.reduce((sum, t) => sum + t.v.u * t.jars.length, 0);
    return { items, updates, n, tier, total, over };
  }

  /** The copy the plan comes from still is the cart: the same lines with the same quantities (`items` from /cart.js). */
  function isLive(d, items) {
    if (!d || !items) return false;
    const live = new Map(items.map((i) => [String(i.key), i.quantity]));
    return d.lines.length === live.size && d.lines.every((l) => live.get(String(l.key)) === l.qty);
  }

  /**
   * One guarded pass for a set. `io`: lock(task) shared by every page, live() → the cart's items, data() → the copy,
   * refresh() → resolves once the copy is the cart again, add(items) → the response, update(updates, plan) → resolves once
   * the copy shows the update (plan is null for taking added lines out again).
   * @returns {Promise<'stale'|null|object>} 'stale' = the copy was old, refreshed, nothing written; null = nothing to do;
   * otherwise the plan that was written.
   */
  // what an added line is, whatever key Shopify gives it: the variant, the set number and the engine's mark
  const idOf = (v, props) => `${v}|${(props && props._nr) || ''}|${(props && props._sb) || ''}`;
  const held = (items, id) => items.filter((i) => idOf(i.variant_id, i.properties) === id).reduce((s, i) => s + i.quantity, 0);

  /**
   * A swap a page started and did not finish: when its add landed, take out what it was taking out. The page may have gone
   * away with the add still on its way (3 Nov, real cart: it landed after the next page had looked, and 8 jars became 16),
   * so a fresh record gets a few seconds to show up in the cart before it counts as never sent.
   * @returns {Promise<false|'done'|'waited'>} 'done' = it wrote; 'waited' = the cart changed under it, read it again.
   */
  async function finish(io, def, items) {
    const rec = io.journal.get();
    if (!rec || rec.set !== def.id) return false;
    const landed = (its) => rec.adds.every((a) => held(its, a.id) >= a.before + a.q);
    let now = items;
    let waited = false;
    for (let i = 0; i < 8 && !landed(now) && io.sleep && Date.now() - rec.at < 6000; i += 1) {
      await io.sleep(700);
      now = (await io.live()) || now;
      waited = true;
    }
    io.journal.clear();
    if (!landed(now)) return waited ? 'waited' : false;
    const updates = {};
    Object.entries(rec.upd).forEach(([k, [before, target]]) => {
      const it = now.find((i) => String(i.key) === k);
      // a line the shopper changed since is left as it is
      if (it && it.quantity === before) updates[k] = target;
    });
    if (!Object.keys(updates).length) return waited ? 'waited' : false;
    await io.update(updates, null);
    return 'done';
  }

  function step(io, def, opts = {}) {
    return io.lock(async () => {
      const items = await io.live();
      // no cart to compare with (a refused read): stop here; refreshing and calling it 'stale' only asked again at once (3 Nov, 429)
      if (!items) throw new Error('cart read failed');
      if (io.journal && (await finish(io, def, items))) {
        await io.refresh();
        return 'stale';
      }
      if (!isLive(io.data(), items)) {
        await io.refresh();
        return 'stale';
      }
      const p = plan(io.data(), def, opts);
      if (!p) return null;
      if (io.journal && p.items.length) {
        const qty = new Map(items.map((i) => [String(i.key), i.quantity]));
        io.journal.set({
          set: def.id,
          at: Date.now(),
          adds: p.items.map((i) => {
            const id = idOf(i.id, i.properties);
            return { id, q: i.quantity, before: held(items, id) };
          }),
          upd: Object.fromEntries(Object.entries(p.updates).map(([k, q]) => [k, [qty.get(String(k)), q]])),
        });
      }
      const added = p.items.length ? await io.add(p.items) : null;
      try {
        await io.update(p.updates, p);
      } catch (err) {
        const keys = ((added && added.items) || []).map((x) => x.key);
        if (keys.length) await io.update(Object.fromEntries(keys.map((k) => [k, 0])), null).catch(() => {});
        if (io.journal) io.journal.clear();
        throw err;
      }
      if (io.journal) io.journal.clear();
      return p;
    });
  }

  window.SBSets = { tierOf, topOf, shape, unitFor, plan, isLive, step, unescapeHtml };
})();
